// caminho: apps/web/src/configs/RoutesConfig.js
// data e hora da modificação: 2026-04-13 01:50 (Horário de Brasília)

import React from "react";
import { AUTH_PREFIX_PATH, APP_PREFIX_PATH } from "configs/AppConfig";
import PermissionGuard from "components/auth/PermissionGuard";
import { PERMISSIONS } from "constants/permissions";

// Auth
const Login = React.lazy(() => import("views/auth-views/authentication/login"));
const Register = React.lazy(() => import("views/auth-views/authentication/register"));
const ForgotPassword = React.lazy(() =>
  import("views/auth-views/authentication/forgot-password"),
);

// App
const DashboardDefault = React.lazy(() =>
  import("views/app-views/dashboards/default"),
);
const Users = React.lazy(() => import("views/app-views/users"));
const UsersForm = React.lazy(() => import("views/app-views/users/form"));
const UserPermissions = React.lazy(() =>
  import("views/app-views/users/permissions"),
);

const ProfileIndex = React.lazy(() =>
  import("views/app-views/profile"),
);

// Eventos
const EventsIndex = React.lazy(() => import("views/app-views/events"));
const EventsForm = React.lazy(() => import("views/app-views/events/form"));
const EventsDetails = React.lazy(() => import("views/app-views/events/details"));
const EventRegistrationsIndex = React.lazy(() =>
  import("views/app-views/events/registrations"),
);
const EventCheckinIndex = React.lazy(() =>
  import("views/app-views/events/checkin"),
);
const EventDashboard = React.lazy(() =>
  import("views/app-views/events/dashboard"),
);
const EventReportsIndex = React.lazy(() =>
  import("views/app-views/events/reports"),
);
const EventTeamIndex = React.lazy(() =>
  import("views/app-views/events/team"),
);
const EventTotemSettingsPage = React.lazy(() =>
  import("views/app-views/events/totem-settings"),
);
const EventLabelsIndex = React.lazy(() =>
  import("views/app-views/events/labels"),
);
const EventPublicForm = React.lazy(() =>
  import("views/app-views/events/public-form"),
);
const EventEmailTemplateIndex = React.lazy(() =>
  import("views/app-views/events/email-template"),
);
const EventEmailLogs = React.lazy(() =>
  import("views/app-views/events/email-logs"),
);
const EmailMonitorPage = React.lazy(() =>
  import("views/app-views/communication/email-monitor"),
);
const EventEmailCampaigns = React.lazy(() =>
  import("views/app-views/events/email-campaigns"),
);
const EventPublicPage = React.lazy(() =>
  import("views/app-views/events/public-page"),
);
const EventLandingPageEditor = React.lazy(() =>
  import("views/app-views/events/landing-pages"),
);

const PuckLandingEditor = React.lazy(() =>
  import("views/app-views/events/landing-pages/puck/PuckLandingEditor"),
);

const PublicPageResolver = React.lazy(() =>
  import("views/app-views/events/public-page/PublicPageResolver"),
);
const EventPrintStationsMonitor = React.lazy(() =>
  import("views/app-views/events/print-stations-monitor"),
);
const EventAccessControlIndex = React.lazy(() =>
  import("views/app-views/events/access-control"),
);
const EventCertificatesPage = React.lazy(() =>
  import("views/app-views/events/certificates"),
);

// App Mobile Access
const MobileAccessEventsIndex = React.lazy(() =>
  import("views/app-views/mobile-access/events"),
);
const MobileAccessSectorsIndex = React.lazy(() =>
  import("views/app-views/mobile-access/sectors"),
);
const MobileAccessScannerIndex = React.lazy(() =>
  import("views/app-views/mobile-access/scanner"),
);

// Expositores
const EventExhibitorsIndex = React.lazy(() =>
  import("views/app-views/events/exhibitors"),
);
const EventExhibitorUsers = React.lazy(() =>
  import("views/app-views/events/exhibitors/users"),
);
const EventExhibitorImports = React.lazy(() =>
  import("views/app-views/events/exhibitors/imports"),
);
const EventExhibitorLeads = React.lazy(() =>
  import("views/app-views/events/exhibitors/leads"),
);
const EventExhibitorUnmatched = React.lazy(() =>
  import("views/app-views/events/exhibitors/unmatched"),
);

// Públicos
const PublicEventRegistration = React.lazy(() =>
  import("views/public-views/events/registration"),
);
const PublicEventEmbed = React.lazy(() =>
  import("views/public-views/events/embed"),
);
const PublicEventRegistrationSuccess = React.lazy(() =>
  import("views/public-views/events/registration-success"),
);
const PublicEventSuccess = React.lazy(() =>
  import("views/public-views/events/success"),
);
const PublicEventTotem = React.lazy(() =>
  import("views/public-views/events/totem"),
);
const PublicEventTotemIdle = React.lazy(() =>
  import("views/public-views/events/totem-idle"),
);
const PublicEventPrintView = React.lazy(() =>
  import("views/public-views/events/print-view"),
);

// Admin
const MailSettingsIndex = React.lazy(() =>
  import("views/app-views/settings/mail"),
);
const SymplaAdmin = React.lazy(() =>
  import("views/app-views/integrations/sympla"),
);
const WhatsAppSettings = React.lazy(() => import("views/app-views/settings/whatsapp"));
const EventWhatsApp = React.lazy(() => import("views/app-views/events/whatsapp"));

// Guard
const withPermission = (Component, permission) => {
  return function GuardedComponent(props) {
    return (
      <PermissionGuard permission={permission}>
        <Component {...props} />
      </PermissionGuard>
    );
  };
};

// =====================
// ROTAS PÚBLICAS
// =====================
export const publicRoutes = [
  {
    key: "login",
    path: `${AUTH_PREFIX_PATH}/login`,
    component: Login,
  },
  {
    key: "register",
    path: `${AUTH_PREFIX_PATH}/register`,
    component: Register,
  },
  {
    key: "forgot-password",
    path: `${AUTH_PREFIX_PATH}/forgot-password`,
    component: ForgotPassword,
  },

  // Eventos públicos
  {
    key: "public-event-registration",
    path: "/e/:slug",
    component: PublicPageResolver,
  },
  {
    key: "public-event-registration-direct",
    path: "/e/:slug/inscricao",
    component: PublicEventRegistration,
  },
  {
    key: "event-public-page",
    path: `${APP_PREFIX_PATH}/events/:eventId/public-page`,
    component: withPermission(
      EventPublicPage,
      PERMISSIONS.EVENT_PUBLIC_PAGE_MANAGE,
    ),
  },
  {
    key: "event-landing-page-editor",
    path: `${APP_PREFIX_PATH}/events/:eventId/landing-page`,
    component: withPermission(
      PuckLandingEditor,
      PERMISSIONS.EVENT_PUBLIC_PAGE_MANAGE,
    ),
  },
  {
    key: "event-landing-page-puck",
    path: `${APP_PREFIX_PATH}/events/:eventId/landing-page/puck`,
    component: withPermission(
      PuckLandingEditor,
      PERMISSIONS.EVENT_PUBLIC_PAGE_MANAGE,
    ),
  },
  {
    key: "event-landing-page-legacy",
    path: `${APP_PREFIX_PATH}/events/:eventId/landing-page/legacy`,
    component: withPermission(
      EventLandingPageEditor,
      PERMISSIONS.EVENT_PUBLIC_PAGE_MANAGE,
    ),
  },
  {
    key: "public-event-embed",
    path: "/embed/:slug",
    component: PublicEventEmbed,
  },
  {
    key: "public-event-registration-success",
    path: "/e/:slug/success",
    component: PublicEventRegistrationSuccess,
  },
  {
    key: "public-event-totem",
    path: "/totem/:slug",
    component: PublicEventTotem,
  },
  {
    key: "public-event-totem-success",
    path: "/totem/:slug/success",
    component: PublicEventSuccess,
  },
  {
    key: "public-event-totem-idle",
    path: "/totem/:slug/idle",
    component: PublicEventTotemIdle,
  },
  {
    key: "totem-print-view",
    path: "/totem/:slug/print-view",
    component: PublicEventPrintView,
    exact: true,
  },
];

// =====================
// ROTAS PROTEGIDAS
// =====================
export const protectedRoutes = [
  {
    key: "whatsapp-settings",
    path: `${APP_PREFIX_PATH}/settings/whatsapp`,
    component: WhatsAppSettings,
  },
  {
    key: "event-whatsapp",
    path: `${APP_PREFIX_PATH}/events/:eventId/whatsapp`,
    component: EventWhatsApp,
  },
  {
    key: "dashboard.default",
    path: `${APP_PREFIX_PATH}/dashboards/default`,
    component: DashboardDefault,
  },

  {
    key: "profile",
    path: `${APP_PREFIX_PATH}/profile`,
    component: ProfileIndex,
  },

  // Usuários
  {
    key: "users",
    path: `${APP_PREFIX_PATH}/users`,
    component: withPermission(Users, PERMISSIONS.USERS_VIEW),
  },
  {
    key: "users-new",
    path: `${APP_PREFIX_PATH}/users/new`,
    component: withPermission(UsersForm, PERMISSIONS.USERS_CREATE),
  },
  {
    key: "users-edit",
    path: `${APP_PREFIX_PATH}/users/edit/:id`,
    component: withPermission(UsersForm, PERMISSIONS.USERS_EDIT),
  },
  {
    key: "users-permissions",
    path: `${APP_PREFIX_PATH}/users/:id/permissions`,
    component: withPermission(UserPermissions, PERMISSIONS.USERS_PERMISSIONS),
  },

  // Eventos
  {
    key: "event-dashboard",
    path: `${APP_PREFIX_PATH}/events/:eventId/dashboard`,
    component: withPermission(EventDashboard, PERMISSIONS.EVENT_DASHBOARD_VIEW),
  },
  {
    key: "event-registrations",
    path: `${APP_PREFIX_PATH}/events/:eventId/registrations`,
    component: withPermission(
      EventRegistrationsIndex,
      PERMISSIONS.EVENT_REGISTRATION_VIEW,
    ),
  },
  {
    key: "event-checkin",
    path: `${APP_PREFIX_PATH}/events/:eventId/checkin`,
    component: withPermission(EventCheckinIndex, PERMISSIONS.EVENT_CHECKIN_USE),
  },
  {
    key: "event-access-control",
    path: `${APP_PREFIX_PATH}/events/:eventId/access-control`,
    component: withPermission(
      EventAccessControlIndex,
      PERMISSIONS.EVENT_CHECKIN_USE,
    ),
  },
  {
    key: "event-reports",
    path: `${APP_PREFIX_PATH}/events/:eventId/reports`,
    component: withPermission(EventReportsIndex, PERMISSIONS.EVENT_REPORT_VIEW),
  },
  {
    key: "event-certificates",
    path: `${APP_PREFIX_PATH}/events/:eventId/certificates`,
    component: withPermission(
      EventCertificatesPage,
      PERMISSIONS.EVENT_DETAILS_VIEW,
    ),
  },
  {
    key: "event-details",
    path: `${APP_PREFIX_PATH}/events/:eventId/details`,
    component: withPermission(EventsDetails, PERMISSIONS.EVENT_DETAILS_VIEW),
  },
  {
    key: "event-email-logs",
    path: `${APP_PREFIX_PATH}/events/:eventId/email-logs`,
    component: withPermission(EventEmailLogs, PERMISSIONS.EVENT_EMAIL_LOG_VIEW),
  },
  {
    key: "event-team",
    path: `${APP_PREFIX_PATH}/events/:eventId/team`,
    component: withPermission(EventTeamIndex, PERMISSIONS.EVENT_TEAM_VIEW),
  },
  {
    key: "event-email-template",
    path: `${APP_PREFIX_PATH}/events/:eventId/email-template`,
    component: withPermission(
      EventEmailTemplateIndex,
      PERMISSIONS.EVENT_EMAIL_TEMPLATE_MANAGE,
    ),
  },
  {
    key: "event-public-form",
    path: `${APP_PREFIX_PATH}/events/:eventId/public-form`,
    component: withPermission(
      EventPublicForm,
      PERMISSIONS.EVENT_PUBLIC_FORM_MANAGE,
    ),
  },
  {
    key: "event-totem-settings",
    path: `${APP_PREFIX_PATH}/events/:eventId/totem-settings`,
    component: withPermission(
      EventTotemSettingsPage,
      PERMISSIONS.EVENT_TOTEM_MANAGE,
    ),
  },
  {
    key: "event-labels",
    path: `${APP_PREFIX_PATH}/events/:eventId/labels`,
    component: withPermission(
      EventLabelsIndex,
      PERMISSIONS.EVENT_LABELS_MANAGE,
    ),
  },
  {
    key: "event-print-stations-monitor",
    path: `${APP_PREFIX_PATH}/events/:eventId/print-stations/monitor`,
    component: withPermission(
      EventPrintStationsMonitor,
      PERMISSIONS.EVENT_DASHBOARD_VIEW,
    ),
  },

  // App Mobile Access
  {
    key: "mobile-access-events",
    path: `${APP_PREFIX_PATH}/mobile-access`,
    component: withPermission(
      MobileAccessEventsIndex,
      PERMISSIONS.EVENT_CHECKIN_USE,
    ),
  },
  {
    key: "mobile-access-sectors",
    path: `${APP_PREFIX_PATH}/mobile-access/events/:eventId/sectors`,
    component: withPermission(
      MobileAccessSectorsIndex,
      PERMISSIONS.EVENT_CHECKIN_USE,
    ),
  },
  {
    key: "mobile-access-scanner",
    path: `${APP_PREFIX_PATH}/mobile-access/events/:eventId/scan/:zoneId`,
    component: withPermission(
      MobileAccessScannerIndex,
      PERMISSIONS.EVENT_CHECKIN_USE,
    ),
  },

  // Expositores
  {
    key: "event-exhibitors",
    path: `${APP_PREFIX_PATH}/events/:eventId/exhibitors`,
    component: withPermission(
      EventExhibitorsIndex,
      PERMISSIONS.EVENT_EXHIBITOR_VIEW,
    ),
    exact: true,
  },
  {
    key: "event-exhibitor-users",
    path: `${APP_PREFIX_PATH}/events/:eventId/exhibitors/users`,
    component: withPermission(
      EventExhibitorUsers,
      PERMISSIONS.EVENT_EXHIBITOR_USER_VIEW,
    ),
  },
  {
    key: "event-exhibitor-imports",
    path: `${APP_PREFIX_PATH}/events/:eventId/exhibitors/imports`,
    component: withPermission(
      EventExhibitorImports,
      PERMISSIONS.EVENT_EXHIBITOR_IMPORT_VIEW,
    ),
  },
  {
    key: "event-exhibitor-leads",
    path: `${APP_PREFIX_PATH}/events/:eventId/exhibitors/leads`,
    component: withPermission(
      EventExhibitorLeads,
      PERMISSIONS.EVENT_EXHIBITOR_LEAD_VIEW,
    ),
  },
  {
    key: "event-exhibitor-unmatched",
    path: `${APP_PREFIX_PATH}/events/:eventId/exhibitors/unmatched`,
    component: withPermission(
      EventExhibitorUnmatched,
      PERMISSIONS.EVENT_EXHIBITOR_UNMATCHED_VIEW,
    ),
  },

  // Admin
  {
    key: "events-new",
    path: `${APP_PREFIX_PATH}/events/new`,
    component: withPermission(EventsForm, PERMISSIONS.EVENTS_CREATE),
  },
  {
    key: "events-edit",
    path: `${APP_PREFIX_PATH}/events/edit/:id`,
    component: withPermission(EventsForm, PERMISSIONS.EVENTS_EDIT),
  },
  {
    key: "events",
    path: `${APP_PREFIX_PATH}/events`,
    component: withPermission(EventsIndex, PERMISSIONS.EVENTS_VIEW),
    exact: true,
  },
  {
    key: "mail-settings",
    path: `${APP_PREFIX_PATH}/settings/mail`,
    component: withPermission(
      MailSettingsIndex,
      PERMISSIONS.COMMUNICATION_MANAGE,
    ),
  },
  {
    key: "sympla-admin",
    path: `${APP_PREFIX_PATH}/integrations/sympla`,
    component: withPermission(SymplaAdmin, PERMISSIONS.INTEGRATIONS_VIEW),
  },
  {
    key: "communication-email-campaigns",
    path: `${APP_PREFIX_PATH}/communication/email-campaigns`,
    component: withPermission(
      EventEmailCampaigns,
      PERMISSIONS.COMMUNICATION_VIEW,
    ),
  },
  {
    key: "communication-email-monitor",
    path: `${APP_PREFIX_PATH}/communication/email-monitor`,
    component: withPermission(
      EmailMonitorPage,
      PERMISSIONS.COMMUNICATION_VIEW,
    ),
  },
];
